/**
 * useRequireAuth — gate an in-app action behind authentication.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decisions D3, D5.
 *
 * Usage:
 *   const { requireAuth } = useRequireAuth();
 *
 *   // gate a navigation
 *   <button onClick={() => requireAuth({
 *     type: "navigate",
 *     to: "/me/orders",
 *     mode: "openActivity",
 *   })}>주문 보기</button>
 *
 *   // gate a callback
 *   const { requireAuth } = useRequireAuth();
 *   <button onClick={() => requireAuth(
 *     { type: "callback", actionId: "submit-payment" },
 *     () => submitPayment(),
 *   )}>결제</button>
 *
 * Behavior:
 *   - Authenticated → run onAuthed (or execute the intent inline) immediately.
 *   - Not authenticated → save intent to sessionStorage (D5 idempotent slot)
 *     and open AuthActivity above the current screen. The login flow
 *     consumes the intent on success.
 *
 * Callback intents (`type: "callback"`) require an `onAuthed` argument
 * because the function reference cannot survive an OAuth round trip; the
 * actionId in the stored intent is informational only.
 */

import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useActivityNavigator } from "./useActivityNavigator";
import { setPendingAuthIntent, setAuthReturnPath } from "./pendingAuthIntent";
import type { PendingAuthIntent } from "./types";

export interface RequireAuthApi {
  requireAuth: (intent: PendingAuthIntent, onAuthed?: () => void) => void;
}

export function useRequireAuth(): RequireAuthApi {
  const navigator = useActivityNavigator();
  const location = useLocation();

  return {
    requireAuth: useCallback(
      (intent, onAuthed) => {
        const isAuthed = !!useAuthStore.getState().accessToken;

        if (isAuthed) {
          if (onAuthed) {
            onAuthed();
          } else if (intent.type === "navigate") {
            executeNavigateIntent(intent, navigator);
          }
          return;
        }

        // Not authed: stash intent + open AuthActivity over current screen.
        // 독립=Y — caller 의 현재 path 로 fallback 해서 X 닫기 시 호출자 화면으로 복귀.
        const sourcePath = location.pathname + location.search;
        setPendingAuthIntent(intent);
        // OAuth web flow 는 vendor redirect 다음 callback 으로 돌아오면 활동 스택이
        // 비어있어 auth activity 의 fallbackPath 를 알 수 없다. sessionStorage 에 따로
        // 박아둬서 OAuthCallbackPage 가 원래 화면으로 돌려보낸다.
        setAuthReturnPath(sourcePath);
        navigator.open({
          kind: "auth",
          presentation: "fullscreen",
          appBarAction: "close",
          url: "/login",
          fallbackPath: sourcePath,
        });
      },
      [navigator, location.pathname, location.search],
    ),
  };
}

/**
 * Execute a "navigate" pending intent through the navigator.
 * Exported so OAuth callback / login success paths can run it after
 * consuming the intent.
 */
export function executeNavigateIntent(
  intent: Extract<PendingAuthIntent, { type: "navigate" }>,
  navigator: ReturnType<typeof useActivityNavigator>,
): void {
  switch (intent.mode) {
    case "pushEntry":
      navigator.push({ url: intent.to });
      return;
    case "replaceActivity":
      navigator.replaceActivity({
        kind: "intent",
        presentation: "overlay",
        appBarAction: "back",
        url: intent.to,
        fallbackPath: "/",
      });
      return;
    case "mainTab":
      // 메인 탭으로 이동 — 활동 스택 비우고 URL 교체.
      navigator.closeAllTo(intent.to);
      return;
    case "openActivity":
    default:
      navigator.open({
        kind: "intent",
        presentation: "overlay",
        appBarAction: "back",
        url: intent.to,
        fallbackPath: "/",
      });
      return;
  }
}
